import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthShell, AuthField } from "@/components/AuthShell";
import { useAuth, formatApiError } from "@/context/AuthContext";

export default function Register() {
  const [role, setRole] = useState("client");
  const [form, setForm] = useState({ name: "", email: "", password: "", company: "" });
  const [agreeContractor, setAgreeContractor] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (role === "partner" && !agreeContractor) {
      setError("Please acknowledge the independent contractor terms to continue.");
      return;
    }
    setLoading(true);
    try {
      const user = await register({ ...form, role, agreed_contractor: role === "partner" ? agreeContractor : false });
      if (user.role !== "partner" && params.get("next") === "baseline-review") {
        navigate("/client?service=baseline-review");
      } else {
        navigate(user.role === "partner" ? "/partner" : "/client");
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Create your account"
      subtitle="Choose how you'd like to work with Aliwer."
      image="https://static.prod-images.emergentagent.com/jobs/a7857c63-2010-4de3-a354-ef80fc69c639/images/29c5a35a6953ad6be50a2bfca7e8fe84874ff7ea36a30de0c488e32f298c2cca.png"
      foot={
        <>
          Already registered?{" "}
          <Link to="/login" className="text-paper underline underline-offset-4" data-testid="to-login-link">
            Log in
          </Link>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-2 p-1 rounded-full border border-white/15 mb-6" data-testid="role-toggle">
        {[
          { k: "client", label: "I'm a Client" },
          { k: "partner", label: "I'm a Partner" },
        ].map((r) => (
          <button
            key={r.k}
            type="button"
            onClick={() => setRole(r.k)}
            data-testid={`role-${r.k}`}
            className={`rounded-full py-2 text-sm font-medium transition-colors ${
              role === r.k ? "bg-paper text-black" : "text-fog hover:text-paper"
            }`}
          >
            {r.label}
          </button>
        ))}
      </div>

      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <AuthField label="Full name" value={form.name} onChange={set("name")} required testid="register-name" />
        <AuthField label="Email" type="email" value={form.email} onChange={set("email")} required testid="register-email" />
        <AuthField
          label={role === "partner" ? "Company / Practice (optional)" : "Company (optional)"}
          value={form.company}
          onChange={set("company")}
          testid="register-company"
        />
        <AuthField label="Password" type="password" value={form.password} onChange={set("password")} required minLength={6} testid="register-password" />
        {role === "partner" && (
          <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="contractor-ack-label">
            <input
              type="checkbox"
              checked={agreeContractor}
              onChange={(e) => setAgreeContractor(e.target.checked)}
              data-testid="contractor-ack"
              className="mt-0.5 h-4 w-4 shrink-0 accent-white bg-transparent border border-white/40 rounded cursor-pointer"
            />
            <span className="text-xs text-fog leading-relaxed">
              I understand that Partners engage with Aliwer as{" "}
              <span className="text-paper">independent contractors</span>, not employees,
              and I agree to the{" "}
              <Link to="/terms" target="_blank" className="text-paper underline underline-offset-2">Terms of Service</Link>{" "}
              and{" "}
              <Link to="/privacy" target="_blank" className="text-paper underline underline-offset-2">Privacy Policy</Link>.
            </span>
          </label>
        )}
        {role === "client" && (
          <p className="text-xs text-fog leading-relaxed" data-testid="client-terms-note">
            By creating an account you agree to our{" "}
            <Link to="/terms" target="_blank" className="text-paper underline underline-offset-2">Terms</Link>{" "}
            and{" "}
            <Link to="/privacy" target="_blank" className="text-paper underline underline-offset-2">Privacy Policy</Link>.
          </p>
        )}
        {error && (
          <p className="text-sm text-red-400" data-testid="register-error">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={loading}
          data-testid="register-submit"
          className="w-full rounded-full bg-paper text-black text-sm font-medium py-3.5 hover:bg-white transition-colors disabled:opacity-60"
        >
          {loading ? "Creating account…" : `Create ${role} account`}
        </button>
      </form>
    </AuthShell>
  );
}
